import 'antd/dist/antd.css'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { message, Tooltip } from 'antd'
import { epagAPI } from '../../API/epagAPI'
import { useSave } from '../../Context/saveContext'
import { Text } from '../../constants/TextConstants'
import { OrangeColor } from '../../constants/constants'
import { useGetToken } from '../../hooks/useGetToken'
import {
  arrayWithObjectsToText,
  displayDate,
  displayUTCDate,
  formatPercentage,
  getHeightBox,
  sortByStartedDate,
  useNavigateToWhenOwnView,
  cleanString
} from '../../utils/utils'
import { DataBlock } from '../DataBlock/index'
import { ErrorText, WrapperImgTag } from '../EditProfile/Inputs/styles'
import { HeaderMenu } from '../HeaderMenu'
import { LoadingSpin } from '../LoadingSpin'
import { TableExperience } from '../TableExperience/index'
import { emptyProfile } from './emptyProfile'
import {
  BoldText,
  ProfileActionButton,
  CompanyProfile,
  EmployeeName,
  HeaderProfileWrapper,
  InfoColum1,
  InfoColum2,
  InfoWrapper,
  Line,
  ProfessionalExperienceTitle,
  ProfileWrapper,
  RoleDegree,
  SinceDate,
  SectionTitle,
  SectionLastUpdate,
  ActionContainer,
  UpdateProfileButtonLabel,
  UpdateProfileButtonIcon,
  CopyProfileButtonIcon,
  Profile,
  ImageContainer,
  SubtitlesWrapper
} from './styles'

import HeaderLinks from '../HeaderLinks'
import { ExportProfile } from '../Modals/ExportProfile'
import { PERMISSION_CODES } from '../../constants/permissions'

export const ViewProfile = ({ hideEditButton = false, hideHeaderMenu = true }) => {
  const [profile, setProfile] = useState(emptyProfile)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingText, setLoadingText] = useState(Text.GenericLoading)
  const [ErrorFetch, setErrorFetch] = useState('')
  const [copiedProfile, setCopiedProfile] = useState(false)
  const { token } = useGetToken()
  const { setProfileEmail, userProfile, userPermissions } = useSave()
  const { userId } = useParams()
  const isOwnProfile = !userId
  const navigate = useNavigate()
  useNavigateToWhenOwnView(userId, '/view')

  useEffect(() => {
    setIsLoading(true)
    if (token && userProfile) {
      if (isOwnProfile) {
        setProfile(userProfile)
        setProfileEmail(userProfile.email)
        setIsLoading(false)
        setErrorFetch('')
      } else {
        epagAPI
          .GetUsersProfile(`${userId}@endava.com`, token)
          .then((result) => {
            if (result === null) {
              setIsLoading(false)
              setProfile(emptyProfile)
            }
            if (result) {
              setProfile(result)
              setIsLoading(false)
              setErrorFetch('')
            } else {
              setErrorFetch('')
              navigate('/NotFound', { replace: true })
            }
          })
          .catch((error) => {
            setErrorFetch(error.message)
            navigate('/Forbidden', { replace: true })
          })
      }
    }
  }, [token, userProfile, userId])

  const onSubmit = () => {
    setIsLoading(true)
    if (token && userProfile) {
      epagAPI
        .UpdateProfile(token)
        .then((response) => {
          if (response) {
            userProfile.role = response.role
            userProfile.grade = response.grade
            userProfile.core_skill = response.core_skill
            userProfile.since = response.since
            setProfile(userProfile)
            setIsLoading(false)
            setErrorFetch('')
            message.success(Text.ProfileParametersUpdatedSuccessMessage, 4)
          } else {
            setProfile(userProfile)
            setIsLoading(false)
            setErrorFetch('')
            message.info(Text.ProfileParametersUpdatedMessage, 4)
          }
        })
        .catch((error) => {
          setErrorFetch(error.message)
          setIsLoading(false)
        })
    }
  }
  const onClickCopy = async (email) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/view/${email.split('@')[0].toLowerCase()}`)
      setCopiedProfile(true)
      setTimeout(() => {
        setCopiedProfile(false)
      }, 1000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const getRepositoryLinks = (repository) => ({
    repository_links: `
    <p>LinkedIn: <a href='${cleanString(repository.linkedin_link)}' target='_blank'>${repository.linkedin_link}</a>&nbsp;
      Github: <a href='${cleanString(repository.github_link)}' target='_blank'>${repository.github_link}</a></p>`
  })

  const RenderProfile = () => {
    return (
      <>
        {isOwnProfile && (
          <>
            <SectionTitle>{Text.MyProfile}</SectionTitle>
            <SectionLastUpdate>
              Last Update: {displayDate(profile.logs ? profile.logs.pop()?.date : '')}
            </SectionLastUpdate>
          </>
        )}
        {!hideHeaderMenu && (
          <HeaderMenu mail={profile.email} defaultNavName={Text.ViewProfile} hideEditButton={hideEditButton} />
        )}
        {profile.name !== '' ? (
          <div>
            <ProfileWrapper>
              <SubtitlesWrapper>
                <CompanyProfile>Endava Profile</CompanyProfile>
                <CompanyProfile>
                  {Text.Completion}: {formatPercentage(profile.profile_filling_percentage)}%
                </CompanyProfile>
              </SubtitlesWrapper>
              <HeaderProfileWrapper>
                <Profile>
                  <div>
                    <EmployeeName>
                      {profile.name} {profile.lastName}
                    </EmployeeName>
                    <RoleDegree>
                      {profile.role} - {profile.grade}
                    </RoleDegree>
                    {profile.since && profile.since !== '' ? (
                      <SinceDate>
                        {Text.SinceDate} - {displayUTCDate(profile.since)}
                      </SinceDate>
                    ) : (
                      ''
                    )}
                  </div>
                  {profile.profile_image && (
                    <Tooltip title={Text.ProfileImageTooltipMessage} placement='top'>
                      <ImageContainer>
                        <WrapperImgTag>
                          <img
                            src={`${profile.profile_image}?${performance.now()}`}
                            alt={Text.ProfileImage}
                            draggable="false" />
                        </WrapperImgTag>
                      </ImageContainer>
                    </Tooltip>
                  )}
                </Profile>
                <ActionContainer>
                  {!isOwnProfile && <HeaderLinks email={profile.email} active={'view'} />}

                  {copiedProfile ? (
                    <Tooltip title={Text.CopiedProfileUrl}>
                      <ProfileActionButton type='primary'>
                        <CopyProfileButtonIcon size={20} />
                      </ProfileActionButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={Text.CopyProfileUrl}>
                      <ProfileActionButton type='primary' onClick={() => onClickCopy(profile.email)}>
                        <CopyProfileButtonIcon size={20} />
                      </ProfileActionButton>
                    </Tooltip>
                  )}
                  {isOwnProfile && userPermissions.includes(PERMISSION_CODES.useUpdateButton.code) && (
                    <Tooltip title={Text.UpdateValuesFromEwaPlaceholder} placement='top'>
                      <ProfileActionButton type='primary' onClick={() => onSubmit()}>
                        <UpdateProfileButtonIcon size={20} />
                        <UpdateProfileButtonLabel>{Text.Update}</UpdateProfileButtonLabel>
                      </ProfileActionButton>
                    </Tooltip>
                  )}
                  <ExportProfile
                    profileToExport={profile}
                    setIsLoading={setIsLoading}
                    setLoadingText={setLoadingText}
                  />
                </ActionContainer>
              </HeaderProfileWrapper>
              <Line />
              <InfoWrapper>
                <InfoColum1>
                  <DataBlock title='Core Skill' body={profile.core_skill} />
                  <DataBlock
                    title='Industry Verticals'
                    body={arrayWithObjectsToText(profile.domains)}
                    heightBox={getHeightBox(profile.domains)}
                  />
                  <DataBlock
                    tooltip={Text.TechnicalSkillsTooltip}
                    title='Technical Skills'
                    body={arrayWithObjectsToText(profile.technical_skills)}
                    heightBox={getHeightBox(profile.technical_skills)}
                  />
                  <DataBlock
                    title='Programming Languages'
                    body={arrayWithObjectsToText(profile.programming_languages)}
                    heightBox={getHeightBox(profile.programming_languages)}
                  />
                  <DataBlock
                    title='Databases'
                    body={arrayWithObjectsToText(profile.databases)}
                    heightBox={getHeightBox(profile.databases)}
                  />
                  <DataBlock
                    title={Text.Frameworks}
                    body={arrayWithObjectsToText(profile.technologies_and_frameworks)}
                    heightBox={getHeightBox(profile.technologies_and_frameworks)}
                  />
                </InfoColum1>
                <InfoColum2>
                  <DataBlock
                    title='Summary'
                    body={profile.summary}
                    heightBox={profile.summary.length > 0 ? '15px' : '50px'}
                    printEmpty
                  />
                  <BoldText color={OrangeColor}>Qualifications</BoldText>
                  <DataBlock
                    title='Educational Background'
                    color='black'
                    body={arrayWithObjectsToText(profile.educational_background)}
                  />
                  <DataBlock
                    title='Certifications'
                    color='black'
                    body={arrayWithObjectsToText(profile.certification)}
                  />
                  <DataBlock
                    title='Languages'
                    body={arrayWithObjectsToText(profile.languages)}
                    heightBox={getHeightBox(profile.languages)}
                  />
                  <DataBlock
                    title='Soft Skills'
                    body={arrayWithObjectsToText(profile.soft_skills)}
                    heightBox={getHeightBox(profile.soft_skills)}
                  />
                  <DataBlock
                    title='Repository'
                    body={arrayWithObjectsToText([getRepositoryLinks(profile.repository)])}
                    heightBox={getHeightBox(profile.repository)}
                  />
                  <DataBlock
                    title='Hobbies'
                    body={arrayWithObjectsToText(profile.hobbies)}
                    heightBox={getHeightBox(profile.hobbies)}
                  />
                </InfoColum2>
              </InfoWrapper>
            </ProfileWrapper>
            {
              <ProfileWrapper>
                <CompanyProfile>Endava Profile</CompanyProfile>
                <ProfessionalExperienceTitle>PROFESSIONAL EXPERIENCE</ProfessionalExperienceTitle>
                {sortByStartedDate(profile.professional_experience)?.map((dataItem, index) => (
                  <TableExperience data={dataItem} key={'Expereience' + index} />
                ))}
              </ProfileWrapper>
            }
            <div style={{ height: '30px', width: '100%' }}> </div>
          </div>
        ) : (
          <div>{Text.NotProfile}</div>
        )}
        {profile.email !== '' && profile.name === '' ? <div>{Text.NotInfo}</div> : undefined}
      </>
    )
  }
  return (
    <>
      {isLoading ? (
        <LoadingSpin loadingText={loadingText} />
      ) : ErrorFetch !== '' ? (
        <ErrorText>{ErrorFetch}</ErrorText>
      ) : (
        RenderProfile()
      )}
    </>
  )
}

ViewProfile.propTypes = {
  hideEditButton: PropTypes.bool,
  hideHeaderMenu: PropTypes.bool,
  isOwnProfile: PropTypes.bool
}
